<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">角色管理</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 个角色</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="角色名称">
            <el-input v-model="query.keyword" placeholder="名称或编码" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="数据范围">
            <el-select v-model="query.data_scope" placeholder="全部" clearable style="width: 150px">
              <el-option v-for="s in dataScopes" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option label="启用" :value="1" />
              <el-option label="停用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增角色</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="name" label="角色名称" min-width="150"/>
        <el-table-column prop="code" label="角色编码" min-width="150">
          <template slot-scope="scope">
            <code>{{ scope.row.code }}</code>
          </template>
        </el-table-column>
        <el-table-column label="所属公司" min-width="140">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.company_id === 0" size="mini" type="info" effect="plain">系统预置</el-tag>
            <span v-else>{{ companyName(scope.row.company_id) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="数据范围" width="130">
          <template slot-scope="scope">{{ scopeLabel(scope.row.data_scope) }}</template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="70" align="center"/>
        <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip/>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-switch v-model="scope.row.status" :active-value="1" :inactive-value="0" @change="handleStatusChange(scope.row, $event)" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" align="center">
          <template slot-scope="scope">
            <!-- 分配权限：只锁 super_admin（不走角色授权）；预置业务角色（operations_admin 等）
                 全平台共用，仅超管可配，非超管仍禁用 -->
            <el-button type="text" size="mini" :disabled="scope.row.code === 'super_admin' || (!isSuperAdmin && scope.row.company_id === 0)" @click="handleAuth(scope.row)">分配权限</el-button>
            <el-button type="text" size="mini" :disabled="scope.row.company_id === 0" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" :disabled="scope.row.company_id === 0" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="form.id ? '编辑角色' : '新增角色'" :visible.sync="dialogVisible" width="680px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="角色名称" prop="name">
          <el-input v-model="form.name" maxlength="64" />
        </el-form-item>
        <el-form-item label="角色编码" prop="code">
          <el-input v-model="form.code" placeholder="程序内标识，如 partner_admin" maxlength="64" />
        </el-form-item>
        <el-form-item label="所属公司">
          <el-select v-model="form.company_id" style="width: 100%">
            <el-option :label="'系统预置（各公司通用）'" :value="0" />
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="数据范围">
          <el-select v-model="form.data_scope" style="width: 100%">
            <el-option v-for="s in dataScopes" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="999" controls-position="right" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="255" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="停用" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" @click="submit">确 定</el-button>
      </div>
    </el-dialog>

    <el-dialog title="分配权限" :visible.sync="authVisible" width="880px" top="6vh" append-to-body>
      <div class="auth-head">
        <span class="auth-role">角色：<b>{{ currentRole ? currentRole.name : '' }}</b></span>
        <div class="auth-batch" v-if="!isSuperRole">
          <el-button size="mini" @click="batchAll(1)">全部勾选</el-button>
          <el-button size="mini" @click="batchAll(0)">全部清空</el-button>
        </div>
      </div>

      <el-alert
        v-if="isSuperRole"
        title="超级管理员默认拥有全部权限，不可修改"
        type="warning"
        :closable="false"
        show-icon
        class="auth-alert"
      />
      <p class="auth-tip">
        先勾「显示」该角色才能看到这个菜单；勾上后右侧展开 <b>新增 / 修改 / 删除 / 导出 / 导入</b> 权限，决定列表里能用哪些按钮。
      </p>

      <div class="perm-wrap">
        <div class="perm-group" v-for="group in permGroups" :key="group.id">
          <div class="perm-group-head">
            <i v-if="group.icon" :class="group.icon"></i>
            <span class="perm-group-name">{{ group.name }}</span>
            <span class="perm-group-count">
              已选 {{ groupCheckedCount(group) }} / {{ group.children.length }} 个菜单
            </span>
          </div>
          <div class="perm-item" v-for="menu in group.children" :key="menu.id" :class="{ 'perm-item-on': menu.can_view === 1 }">
            <div class="perm-item-name">
              <i v-if="menu.icon" :class="menu.icon"></i>
              <span>{{ menu.name }}</span>
            </div>
            <div class="perm-item-ops">
              <!-- 主开关：是否显示该菜单（等价于 can_view） -->
              <el-checkbox
                :value="menu.can_view === 1"
                class="perm-view-check"
                :disabled="isSuperRole"
                @change="val => handleViewChange(menu, val)"
              >显示</el-checkbox>
              <!-- 勾了显示才展开细粒度权限 -->
              <el-checkbox-group
                v-model="menu.checked"
                class="perm-detail"
                :class="{ 'perm-detail-hidden': menu.can_view !== 1 }"
                :disabled="isSuperRole"
              >
                <el-checkbox-button
                  v-for="f in DETAIL_FIELDS"
                  :key="f.key"
                  :label="f.key"
                  :disabled="isSuperRole"
                  size="mini"
                >{{ f.label }}</el-checkbox-button>
              </el-checkbox-group>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <span class="auth-foot-tip">点击「完成」保存权限</span>
        <el-button type="primary" @click="saveAuth">完 成</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getRoleList, changeRoleStatus, getRoleMenus, saveRoleMenus, createRole, updateRole, deleteRole, getCompanyOptions
} from '@/api/system'

const emptyForm = () => ({
  id: 0, company_id: 0, name: '', code: '', data_scope: 3, sort: 1, remark: '', status: 1
})

// 角色×菜单授权的 6 个操作权限（can_view 由「显示」主开关控制）
const PERM_FIELDS = [
  { key: 'can_view',   label: '查看' },
  { key: 'can_create', label: '新增' },
  { key: 'can_update', label: '修改' },
  { key: 'can_delete', label: '删除' },
  { key: 'can_export', label: '导出' },
  { key: 'can_import', label: '导入' }
]

// 细粒度权限块里展示的项（不含 can_view，它由主开关负责）
const DETAIL_FIELDS = PERM_FIELDS.filter(f => f.key !== 'can_view')

export default {
  name: 'RoleManage',
  data() {
    return {
      loading: false,
      list: [],
      companies: [],
      query: { keyword: '', data_scope: '', status: '' },
      dialogVisible: false,
      authVisible: false,
      currentRole: null,
      form: emptyForm(),
      // 当前打开的角色×菜单授权状态（data，非 computed，避免 watch 死循环）
      permGroups: [],
      dataScopes: [
        { value: 1, label: '全部数据' },
        { value: 2, label: '本公司及下级' },
        { value: 3, label: '仅本公司' },
        { value: 4, label: '仅本人' }
      ],
      rules: {
        name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
        code: [{ required: true, message: '请输入角色编码', trigger: 'blur' }]
      }
    }
  },
  computed: {
    DETAIL_FIELDS() { return DETAIL_FIELDS },
    // 超级管理员 / 系统预置角色：默认拥有全部权限，界面锁死不可改
    isSuperRole() {
      return !!(this.currentRole && this.currentRole.code === 'super_admin')
    },
    // 当前登录账号是否超管（决定预置角色的「分配权限」按钮可用性）
    isSuperAdmin() {
      const u = this.$store.getters.getUserInfo || {}
      return Number(u.is_super) === 1
    }
  },
  created() {
    this.loadData()
    this.loadCompanies()
  },
  methods: {
    // 列表（后端已做 keyword/data_scope/status 筛选；前端无分页器，一次拉足量）
    loadData() {
      this.loading = true
      const params = {}
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.data_scope !== '' && this.query.data_scope !== null) params.data_scope = this.query.data_scope
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      params.page = 1
      params.page_size = 100
      getRoleList(params).then(res => {
        this.list = (res.data && res.data.list) || []
      }).finally(() => {
        this.loading = false
      })
    },
    // 所属公司下拉（走公司管理 options 接口，[{id,name}]）
    loadCompanies() {
      getCompanyOptions().then(res => {
        this.companies = (res.data || [])
      })
    },
    companyName(id) {
      const c = this.companies.find(i => i.id === id)
      return c ? c.name : '—'
    },
    scopeLabel(val) {
      const s = this.dataScopes.find(i => i.value === val)
      return s ? s.label : '—'
    },
    handleSearch() {
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', data_scope: '', status: '' }
      this.loadData()
    },
    // 分配权限：拉后端权限树（已建好菜单树），给每个二级菜单补 checked[] 供细粒度勾选块用
    handleAuth(row) {
      this.currentRole = { id: row.id, code: row.code, name: row.name }
      getRoleMenus(row.id).then(res => {
        const data = res.data || {}
        const role = data.role || {}
        const groups = (data.menus || []).map(g => Object.assign({}, g, {
          children: (g.children || []).map(m => Object.assign({}, m, {
            checked: DETAIL_FIELDS.filter(f => m[f.key] === 1).map(f => f.key)
          }))
        }))
        this.permGroups = groups
        this.authVisible = true
        // 以后端返回的 role.code 为准（系统预置角色标记）
        if (role.code) this.currentRole.code = role.code
      })
    },
    // 分组下已开启显示的菜单数
    groupCheckedCount(group) {
      return group.children.filter(m => m.can_view === 1).length
    },
    // 主开关：显示 / 隐藏该菜单
    handleViewChange(menu, val) {
      if (this.isSuperRole) return
      menu.can_view = val ? 1 : 0
      // 关掉显示时，细粒度权限一并清空（隐藏的菜单留着权限没有意义）
      if (!val) menu.checked = []
    },
    // 批量：全部开启显示并给满权限 / 全部清空
    batchAll(val) {
      if (this.isSuperRole) {
        this.$message.warning('超级管理员默认拥有全部权限，无需配置')
        return
      }
      this.permGroups.forEach(group => {
        group.children.forEach(menu => {
          menu.can_view = val ? 1 : 0
          menu.checked = val ? DETAIL_FIELDS.map(f => f.key) : []
        })
      })
      this.$message.success(val ? '已全部勾选' : '已全部清空')
    },
    // 由当前勾选状态构造 items（一级菜单聚合其子菜单权限，全量提交给后端覆盖）
    buildItems() {
      const items = []
      this.permGroups.forEach(group => {
        const agg = { menu_id: group.id, can_view: group.children.some(m => m.can_view === 1) ? 1 : 0 }
        DETAIL_FIELDS.forEach(f => { agg[f.key] = group.children.some(m => (m.checked || []).includes(f.key)) ? 1 : 0 })
        items.push(agg)
        group.children.forEach(m => {
          const row = { menu_id: m.id, can_view: m.can_view === 1 ? 1 : 0 }
          DETAIL_FIELDS.forEach(f => { row[f.key] = (m.checked || []).includes(f.key) ? 1 : 0 })
          items.push(row)
        })
      })
      return items
    },
    // 保存权限（点「完成」提交；系统预置/超级管理员角色后端会拒，提示由拦截器统一弹）
    saveAuth() {
      if (this.isSuperRole) {
        this.authVisible = false
        return
      }
      const items = this.buildItems()
      saveRoleMenus(this.currentRole.id, { items }).then(() => {
        this.$message.success('权限已保存')
        this.authVisible = false
      })
    },
    handleAdd() {
      this.form = emptyForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.form = Object.assign({}, row)
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleDelete(row) {
      this.$confirm(`确认删除角色「${row.name}」？`, '删除确认', { type: 'warning' }).then(() => {
        deleteRole(row.id).then(() => {
          this.$message.success('已删除')
          this.loadData()
        })
      }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const { id, name, code, company_id, data_scope, sort, remark, status } = this.form
        const data = { name, code, company_id, data_scope, sort, remark, status }
        const req = id ? updateRole(id, data) : createRole(data)
        req.then(() => {
          this.$message.success(id ? '已保存修改' : '新增成功')
          this.dialogVisible = false
          this.loadData()
        })
      })
    },
    // 角色启停（系统预置 / 超级管理员角色后端会拒，失败回滚开关）
    handleStatusChange(row, val) {
      changeRoleStatus(row.id, { status: val }).then(() => {
        this.$message.success(val === 1 ? '已启用' : '已停用')
      }).catch(() => {
        row.status = val === 1 ? 0 : 1
      })
    }
  }
}
</script>

<style scoped>
/* 弹框顶部：角色名 + 批量操作 */
.auth-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.auth-role {
  font-size: 14px;
  color: #606266;
}
.auth-role b {
  color: var(--primary);
  font-size: 15px;
}
.auth-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: #909399;
  line-height: 1.7;
}

.auth-alert {
  margin-bottom: 12px;
}
.auth-alert + .auth-tip {
  margin-top: 0;
}

.auth-foot-tip {
  float: left;
  font-size: 13px;
  color: #c0c4cc;
  line-height: 32px;
}

/* 权限分组卡片 */
.perm-wrap {
  max-height: 460px;
  overflow-y: auto;
  padding-right: 4px;
}

.perm-group {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.perm-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}
.perm-group-head i {
  color: var(--primary);
}
.perm-group-name {
  flex: 0 0 auto;
}
.perm-group-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

/* 单个菜单行：左菜单名，右「显示」+ 细粒度权限 */
.perm-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f5f7fa;
  transition: background 0.15s;
}
.perm-item:last-child {
  border-bottom: none;
}
.perm-item:hover {
  background: #fcfcfc;
}
/* 已开启显示的菜单行：左侧加一条主色标记 */
.perm-item-on {
  background: #fffcfb;
  box-shadow: inset 3px 0 0 var(--primary);
}

.perm-item-name {
  flex: 0 0 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #303133;
}
.perm-item-name i {
  color: #909399;
  font-size: 14px;
}
.perm-item-on .perm-item-name {
  color: #000;
  font-weight: 500;
}

.perm-item-ops {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 主开关：是否显示该菜单 */
.perm-view-check {
  flex: 0 0 auto;
}
.perm-view-check >>> .el-checkbox__label {
  font-size: 13px;
  color: #606266;
  padding-left: 6px;
}

/* 细粒度权限：未开启显示时折叠隐藏 */
.perm-detail {
  display: inline-flex;
  transition: opacity 0.2s;
}
.perm-detail-hidden {
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}

/* 勾选块：默认浅灰描边，选中主色 —— 比 switch 清爽，且自带文字 */
.perm-item-ops >>> .el-checkbox-button__inner {
  padding: 6px 14px;
  font-size: 13px;
  color: #606266;
  background: #fff;
  border-color: #dcdfe6;
  border-radius: 0;
  box-shadow: none;
  transition: all 0.15s;
}

.perm-item-ops >>> .el-checkbox-button:first-child .el-checkbox-button__inner {
  border-radius: 6px 0 0 6px;
}

.perm-item-ops >>> .el-checkbox-button:last-child .el-checkbox-button__inner {
  border-radius: 0 6px 6px 0;
}

.perm-item-ops >>> .el-checkbox-button__inner:hover {
  color: var(--primary);
  border-color: var(--primary-200);
}

.perm-item-ops >>> .el-checkbox-button.is-checked .el-checkbox-button__inner {
  color: #fff;
  background: var(--primary);
  border-color: var(--primary);
  box-shadow: -1px 0 0 0 var(--primary);
}

/* 超级管理员锁定态：保持勾选外观，但降低饱和度并禁止交互 */
.perm-detail >>> .el-checkbox-button.is-disabled.is-checked .el-checkbox-button__inner {
  color: #fff;
  background: var(--primary);
  border-color: var(--primary);
  opacity: 0.72;
  cursor: not-allowed;
}

.perm-view-check.is-disabled >>> .el-checkbox__label,
.perm-view-check >>> .el-checkbox__input.is-disabled + .el-checkbox__label {
  color: #909399;
}
</style>
